import './legacyColor.css';

/**
 * Shared components (CustomButton, CustomTitle, ...) are still handed literal
 * colours ('black', '#000', '#fff', '#5e5e5e', ...) by hundreds of call sites,
 * which bypass the dark-mode token remap. These helpers map the literals that
 * have a token with the SAME light value onto that token, so light mode renders
 * exactly as before and dark mode follows the theme. Anything else (accent
 * colours, other tokens, expressions) is returned untouched.
 */

const normalize = (value?: string): string =>
  (value ?? '').trim().toLowerCase().replace(/\s+/g, '');

const isBlack = (key: string): boolean =>
  key === '#000' || key === '#000000' || key === 'black';

const isWhite = (key: string): boolean =>
  key === '#fff' || key === '#ffffff' || key === 'white';

const TEXT_TOKENS: Record<string, string> = {
  '#5e5e5e': 'var(--text-secondary)',
  '#4b4b4b': 'var(--text-light)',
  '#333': 'var(--zl-text-strong, #333)',
  '#333333': 'var(--zl-text-strong, #333)',
  '#767676': 'var(--zl-text-muted, #767676)',
  'rgb(118,118,118)': 'var(--zl-text-muted, #767676)',
};

const SOFT_BORDERS = ['#e6e6e6', '#e8e8e8', '#d0d0d0', '#d9d9d9'];

/** Backgrounds that are themselves surface colours (white/transparent/light-grey):
 * a literal black label on one of these is paired with its own background and
 * should invert together in dark mode. A fixed accent fill (alert yellows/reds,
 * brand colours, ...) never changes with theme, so black text on it must not
 * flip to near-white and disappear against that same fixed fill. */
const SURFACE_BACKGROUNDS = new Set([
  '',
  'transparent',
  '#eee',
  '#eeeeee',
  '#fff',
  '#ffffff',
  'white',
  '#f3f3f3',
  // Same literal surface as '#f3f3f3' above, just referenced through the
  // token: dozens of call sites pass bgColor='var(--light-gray)' with a
  // literal black label, expecting the usual light-grey-surface/black-text
  // pairing. Without this, the label stayed literal black and went
  // invisible against the token's own dark-mode value.
  'var(--light-gray)',
]);

/** Text colour: legacy literal -> theme token with the same light value. */
export const legacyTextColor = (color?: string): string | undefined => {
  const key = normalize(color);
  return isBlack(key) ? 'var(--text-primary)' : (TEXT_TOKENS[key] ?? color);
};

/** Surface fill (selects, inputs): light-grey and white literals -> surface tokens. */
export const legacyFillColor = (color?: string): string | undefined => {
  const key = normalize(color);
  if (key === '#f3f3f3') return 'var(--light-gray)';
  return isWhite(key) ? 'var(--surface-container)' : color;
};

/** Border shorthand ('1px solid #E6E6E6'): maps its trailing colour, keeps the rest. */
export const legacyBorder = (border?: string): string | undefined =>
  border?.replace(/(#[0-9a-f]{3,6}|black)\s*$/i, (match) => {
    const key = normalize(match);
    if (isBlack(key)) return 'var(--text-primary)';
    if (key === '#e2e2e2') return 'var(--gray-300)';
    return SOFT_BORDERS.includes(key)
      ? `var(--zl-border-soft, ${match})`
      : match;
  });

export interface LegacyButtonColors {
  bgColor?: string;
  color?: string;
  /** Filled with the "ink" colour (black in light): dark mode flips it to a light chip. */
  ink: boolean;
}

const INK_ALT = new Set(['#111', '#111111']);

/** Light-grey pill fills (e.g. "Preview role"): themed through --button-neutral-bg. */
const NEUTRAL_FILLS = new Set(['#eee', '#eeeeee']);

/**
 * Fill + label colours for CustomButton. A black (or ink-token) fill becomes the
 * ink token, and its white/unspecified label becomes the matching surface token
 * (white in light), so the pair inverts together in dark. White labels on every
 * other fill (blue, red, ...) are left alone.
 */
export const legacyButtonColors = (
  bgColor?: string,
  color?: string
): LegacyButtonColors => {
  const bg = normalize(bgColor);
  const ink =
    isBlack(bg) ||
    INK_ALT.has(bg) ||
    bg === 'var(--text-primary)' ||
    bg === 'var(--black)';
  if (!ink) {
    return {
      ink,
      bgColor:
        bg === '#f3f3f3'
          ? 'var(--light-gray)'
          : NEUTRAL_FILLS.has(bg)
            ? 'var(--button-neutral-bg)'
            : bgColor,
      color: SURFACE_BACKGROUNDS.has(bg) ? legacyTextColor(color) : color,
    };
  }
  const label = normalize(color);
  return {
    ink,
    bgColor: INK_ALT.has(bg)
      ? 'var(--zl-btn-ink-alt, #111111)'
      : 'var(--text-primary)',
    color: !label || isWhite(label) ? 'var(--white)' : legacyTextColor(color),
  };
};
